import React, { useEffect, useMemo, useState } from 'react';

export const EVENT_LIST_PAGE_SIZE = 12;

export function sliceEventPage(items = [], page = 1, pageSize = EVENT_LIST_PAGE_SIZE) {
  const safePage = Math.max(1, Number(page) || 1);
  const start = (safePage - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

/** Resets to page 1 whenever `resetKey` changes; clamps if the list shrinks. */
export function useEventListPage(itemCount, resetKey, pageSize = EVENT_LIST_PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(Math.max(0, itemCount) / pageSize));

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  useEffect(() => {
    setPage((p) => (p > pageCount ? pageCount : p));
  }, [pageCount]);

  return { page, setPage, pageCount, pageSize };
}

function pageWindow(page, pageCount, maxButtons = 5) {
  if (pageCount <= maxButtons) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const half = Math.floor(maxButtons / 2);
  let start = Math.max(1, page - half);
  let end = start + maxButtons - 1;
  if (end > pageCount) {
    end = pageCount;
    start = Math.max(1, end - maxButtons + 1);
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/**
 * Compact pager for landing / cup event grids (12 cards ≈ 4 rows × 3 cols).
 */
export default function EventListPagination({
  page,
  pageCount,
  total,
  pageSize = EVENT_LIST_PAGE_SIZE,
  onPageChange,
  className = '',
  scrollToId,
}) {
  const pages = useMemo(() => pageWindow(page, pageCount), [page, pageCount]);

  if (!total || pageCount <= 1) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const go = (next) => {
    const clamped = Math.min(pageCount, Math.max(1, next));
    if (clamped === page) return;
    onPageChange(clamped);
    if (scrollToId && typeof document !== 'undefined') {
      const el = document.getElementById(scrollToId);
      if (el) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
    }
  };

  const btnBase =
    'inline-flex items-center justify-center min-w-[2.25rem] h-9 px-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40 disabled:pointer-events-none';
  const btnIdle =
    `${btnBase} border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:border-red-400 hover:text-red-700 dark:hover:border-red-600 dark:hover:text-red-300`;
  const btnActive =
    `${btnBase} border border-red-600 bg-red-600 text-white shadow-sm`;

  return (
    <nav
      className={`mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${className}`}
      aria-label="Pagination"
    >
      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 tabular-nums">
        Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{from}–{to}</span>
        {' '}of{' '}
        <span className="font-semibold text-gray-800 dark:text-gray-200">{total}</span>
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          className={btnIdle}
          onClick={() => go(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          Prev
        </button>
        {pages[0] > 1 ? (
          <>
            <button type="button" className={btnIdle} onClick={() => go(1)} aria-label="Page 1">
              1
            </button>
            {pages[0] > 2 ? (
              <span className="px-1 text-gray-400 text-sm select-none" aria-hidden>
                …
              </span>
            ) : null}
          </>
        ) : null}
        {pages.map((n) => (
          <button
            key={n}
            type="button"
            className={n === page ? btnActive : btnIdle}
            onClick={() => go(n)}
            aria-label={`Page ${n}`}
            aria-current={n === page ? 'page' : undefined}
          >
            {n}
          </button>
        ))}
        {pages[pages.length - 1] < pageCount ? (
          <>
            {pages[pages.length - 1] < pageCount - 1 ? (
              <span className="px-1 text-gray-400 text-sm select-none" aria-hidden>
                …
              </span>
            ) : null}
            <button
              type="button"
              className={btnIdle}
              onClick={() => go(pageCount)}
              aria-label={`Page ${pageCount}`}
            >
              {pageCount}
            </button>
          </>
        ) : null}
        <button
          type="button"
          className={btnIdle}
          onClick={() => go(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          Next
        </button>
      </div>
    </nav>
  );
}
